/**
 * 招待を受ける /invite?token=（040、docs/29 §4・§5 → #185、docs/74 §3。Figma「招待と会社の追加（10/14）」②）。スマホで開いても読める1枚
 *   1画面1つの操作の3ステップ（2026-10-14 射場さん：50代の経理の方が招待の画面で操作を誤った）：
 *   ① 招待の確認：会社・招待者・氏名・権限・ログインID →「次へ（パスワード設定）」。下に「心当たりがない場合は、この画面を閉じてください。」
 *   ② パスワード設定：ログインID は見るだけ（入れられない。見えない username の欄も置く＝OS のパスワード管理がユーザー名つきで覚える）、
 *      パスワードの欄の右は「表示／隠す」の字、条件は ✓ の箇条書き（8文字以上（現在 n文字）／英字と数字を両方含む／推測されにくい）→「参加」。同意の一文
 *   ③ 参加が完了しました：会社・権限・ログインID →「ホームへ」。補足「パソコンからは {host} に同じログインIDとパスワードで…」
 *   （#186、docs/74 §9：文は業務の言葉に。Figma「招待と会社の追加（10/14）」①〜④ の直した文言）
 *   #287（2026-11-01、docs/100）：画面の「招待」「参加」をやめた＝① 登録内容の確認（追加者）・主ボタン「利用開始」・③ 利用登録が完了しました。URL・API は /invite のまま
 *   使っている人（アカウントがある・ログインしていない）は ② が「ログイン」（いまのパスワードでログインして参加。Google・Apple も）。
 *   ログイン中の本人は ①確認 →「参加」→ ②完了 の2つ。
 *   ② は URL の ?step=2（ブラウザの戻るで ① に戻れる）。
 *   期限切れ・無効・取り消し・使用済みは止めの画面（#186、docs/75 b。components/StopScreen.tsx の InviteStop）＝手順は出さない
 *   招待と違うメールのアカウントでログイン中（Apple の非公開メールなど、#112、docs/43 §3.5）：確認「別のアカウントでログイン中です」→
 *      「このまま参加」（confirm=true でそのアカウントのまま参加）／「別アカウントでログイン」（ログアウトして ① の画面へ）＝#185 でも残す
 */
import { INVITE_JOIN_MSG, MSG, OAUTH_MSG, PASSWORD_MSG, ROLE_LABEL, passwordProblem, type InviteInfo, type Me, type UserRole } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useLogout, setMe } from "../auth.js";
import { OAuthButtons } from "../components/OAuthButtons.jsx";
import { InviteStop } from "../components/StopScreen.jsx";
import { AuthLogo, ErrorText, Help, HiddenUsername, PasswordInput, PasswordRules, SiteFooter, useFieldIds } from "../components/ui.jsx";
import { useProduct } from "../lib/useProduct.js";

const TOKEN_RE = /^[0-9a-f]{64}$/;

/** 手順の印（①確認 ─ ②パスワード ─ ③完了）。cur＝いまの手順（1から）。前の手順は ✓ */
function Steps({ labels, cur }: { labels: string[]; cur: number }) {
  return (
    <ol className="isteps" aria-label={INVITE_JOIN_MSG.stepOf(cur, labels.length)} data-testid="invite-steps">
      {labels.map((l, i) => {
        const n = i + 1;
        const st = n < cur ? "done" : n === cur ? "cur" : "todo";
        return (
          <li key={l} className={`istep is-${st}`} aria-current={n === cur ? "step" : undefined} data-testid="invite-step">
            <span className="istep-dot" aria-hidden="true">
              {st === "done" ? "✓" : n}
            </span>
            <span className="istep-label">{l}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** 見るだけの行（左に名前、右に値。Figma の「招待の内容」） */
function Rows({ rows, testId }: { rows: Array<[string, React.ReactNode, string]>; testId: string }) {
  return (
    <dl className="irows" data-testid={testId}>
      {rows.map(([k, v, id]) => (
        <div className="irow" key={k} data-testid={`invite-row-${id}`}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function InvitePage() {
  const P = useProduct();
  const [sp, setSp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const nav = useNavigate();
  const qc = useQueryClient();
  const ids = useFieldIds(["pw"] as const);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const [done, setDone] = useState<Me | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const q = useQuery({ queryKey: ["invite", token], queryFn: () => api<InviteInfo>("GET", `/auth/invite?token=${encodeURIComponent(token)}`), enabled: TOKEN_RE.test(token), retry: false });
  const info: InviteInfo | undefined = TOKEN_RE.test(token)
    ? q.data
    : { state: "invalid", companyName: "", inviterName: "", email: "", familyName: "", givenName: "", hasAccount: false, loggedInAsInvitee: false, currentEmail: null, role: "member" };
  const logout = useLogout();

  /**
   * 手順の形：初めての人＝確認→パスワード→完了／使っている人＝確認→ログイン→完了／ログイン中の本人＝確認→完了。
   * 参加したあとは招待の情報を読み直すと「ログイン中の本人」になるので、参加した時点の形のまま（joinedMode）
   */
  const [joinedMode, setJoinedMode] = useState<"new" | "login" | "joined" | null>(null);
  const liveMode: "new" | "login" | "joined" = info?.loggedInAsInvitee ? "joined" : info?.hasAccount ? "login" : "new";
  const mode = done && joinedMode ? joinedMode : liveMode;
  const labels =
    mode === "joined"
      ? [INVITE_JOIN_MSG.stepConfirm, INVITE_JOIN_MSG.stepDone]
      : [INVITE_JOIN_MSG.stepConfirm, mode === "login" ? INVITE_JOIN_MSG.stepLogin : INVITE_JOIN_MSG.stepPassword, INVITE_JOIN_MSG.stepDone];
  const step = done ? labels.length : mode !== "joined" && sp.get("step") === "2" ? 2 : 1;

  // 手順が変わったら：② はパスワードの欄へ、ほかは題へ（読み上げが新しい画面を読む）
  useEffect(() => {
    if (!info || info.state !== "ok") return;
    requestAnimationFrame(() => {
      if (step === 2 && !done) document.getElementById(ids.pw)?.focus();
      else heading.current?.focus();
      window.scrollTo(0, 0);
    });
  }, [step, info?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const join = useMutation({
    mutationFn: (body: { token: string; password?: string; loginPassword?: string; confirm?: boolean }) => api<Me>("POST", "/auth/invite/accept", body),
    onMutate: () => setJoinedMode(liveMode),
    onSuccess: async (m) => {
      await setMe(qc, m);
      void qc.invalidateQueries();
      setDone(m);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors) {
        const first = Object.values(e.problem.errors)[0];
        setErr(typeof first === "string" ? first : messageOf(e));
        return;
      }
      setErr(messageOf(e));
    },
  });

  const toStep2 = () => {
    setErr(undefined);
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set("step", "2");
      return n;
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    if (!info || info.state !== "ok" || done || join.isPending) return;
    if (mode === "joined") return join.mutate({ token });
    if (step !== 2) return toStep2();
    if (mode === "login") {
      if (!pw) {
        setErr(INVITE_JOIN_MSG.passwordRequired);
        document.getElementById(ids.pw)?.focus();
        return;
      }
      return join.mutate({ token, loginPassword: pw });
    }
    const weak = passwordProblem(pw, info.email);
    if (weak) {
      setErr(weak);
      document.getElementById(ids.pw)?.focus();
      return;
    }
    join.mutate({ token, password: pw });
  };

  const title = (t: string) => (
    <h1 className="ititle" ref={heading} tabIndex={-1}>
      {t}
    </h1>
  );
  const role = (r: string): string => ROLE_LABEL[(r === "admin" ? "admin" : "member") as UserRole];

  let body: React.ReactNode;
  if (done) {
    // ③ 完了
    body = (
      <>
        <Steps labels={labels} cur={labels.length} />
        <div id="joined" className="ijoined">
          {title(INVITE_JOIN_MSG.doneTitle)}
          <Rows
            testId="invite-done-rows"
            rows={[
              [INVITE_JOIN_MSG.rowCompany, done.tenant.name || INVITE_JOIN_MSG.noCompanyName, "company"],
              [INVITE_JOIN_MSG.rowRole, role(done.user.role), "role"],
              [INVITE_JOIN_MSG.rowLoginId, done.user.email, "login"],
            ]}
          />
        </div>
        <button type="button" className="btn primary big" id="goHome" onClick={() => nav("/home", { replace: true })}>
          {INVITE_JOIN_MSG.home}
        </button>
        <p className="inote" data-testid="invite-pc-note">
          {INVITE_JOIN_MSG.pcNote(P.host)}
        </p>
      </>
    );
  } else if (!info || q.isPending) {
    body = <p className="hint">読み込んでいます…</p>;
  } else if (info.state !== "ok") {
    // 期限切れ・無効・取り消し・使用済み：止めの画面（下の return で枠ごと置き換える）
    body = null;
  } else if (step === 1) {
    // ① 確認
    body = (
      <>
        <Steps labels={labels} cur={1} />
        {title(INVITE_JOIN_MSG.confirmTitle)}
        <Rows
          testId="invite-confirm-rows"
          rows={[
            [INVITE_JOIN_MSG.rowCompany, info.companyName || INVITE_JOIN_MSG.noCompanyName, "company"],
            [INVITE_JOIN_MSG.rowInviter, info.inviterName || `${P.name}の利用者`, "inviter"],
            [INVITE_JOIN_MSG.rowYou, INVITE_JOIN_MSG.you(info.familyName, info.givenName), "you"],
            [INVITE_JOIN_MSG.rowRole, role(info.role), "role"],
            [INVITE_JOIN_MSG.rowLoginId, info.email, "login"],
          ]}
        />
        <ErrorText>{err}</ErrorText>
        {mode === "joined" ? (
          <button type="submit" className="btn primary big" id="joinBtn" disabled={join.isPending}>
            {INVITE_JOIN_MSG.join}
          </button>
        ) : (
          <button type="button" className="btn primary big" id="inviteNext" onClick={toStep2}>
            {mode === "login" ? INVITE_JOIN_MSG.nextLogin : INVITE_JOIN_MSG.nextPassword}
          </button>
        )}
        <p className="inote" data-testid="invite-nothing">
          {INVITE_JOIN_MSG.nothing}
        </p>
      </>
    );
  } else if (mode === "login") {
    // ② ログイン（使っている人）
    body = (
      <>
        <Steps labels={labels} cur={2} />
        {title(INVITE_JOIN_MSG.loginTitle)}
        <p className="hint">{INVITE_JOIN_MSG.loginHint(P.name)}</p>
        <HiddenUsername email={info.email} />
        <Rows testId="invite-login-rows" rows={[[INVITE_JOIN_MSG.rowLoginId, info.email, "login"]]} />
        <div className="field">
          <label htmlFor={ids.pw}>{INVITE_JOIN_MSG.passwordLabel}</label>
          <PasswordInput id={ids.pw} autoComplete="current-password" value={pw} onChange={setPw} />
        </div>
        <ErrorText>{err}</ErrorText>
        <button type="submit" className="btn primary big" id="joinBtn" disabled={join.isPending}>
          {INVITE_JOIN_MSG.loginJoin}
        </button>
        <p className="hint"><Link className="btn text" to="/forgot">{MSG.forgotLink}</Link></p>
        {/* Google・Apple でログインしている人（#112）。ログインのあとこの画面に戻る */}
        <OAuthButtons intent="login" after={`/invite?token=${token}`} />
      </>
    );
  } else {
    // ② パスワードを決める（初めての人）
    body = (
      <>
        <Steps labels={labels} cur={2} />
        <h1 className="ititle" ref={heading} tabIndex={-1}>
          {INVITE_JOIN_MSG.passwordTitle}
          <Help testId="help-invite" text={`パスワードを設定すると、すぐに利用できます。${PASSWORD_MSG.tip}`} />
        </h1>
        {/* ログインID は見るだけ（入れられない）。OS のパスワード管理がユーザー名つきで覚えるよう、見えない username の欄も置く */}
        <HiddenUsername email={info.email} />
        <Rows testId="invite-password-rows" rows={[[INVITE_JOIN_MSG.rowLoginId, info.email, "login"]]} />
        <div className="field">
          <label htmlFor={ids.pw}>{INVITE_JOIN_MSG.passwordLabel}</label>
          <PasswordInput id={ids.pw} autoComplete="new-password" value={pw} onChange={setPw} describedBy="pwRules" />
          <PasswordRules value={pw} email={info.email} id="pwRules" />
        </div>
        <ErrorText>{err}</ErrorText>
        <button type="submit" className="btn primary big" id="joinBtn" disabled={join.isPending}>
          {INVITE_JOIN_MSG.join}
        </button>
        <p className="inote">押すと <Link to="/terms">利用規約</Link>・<Link to="/privacy">プライバシーポリシー</Link>に同意したものとみなします。</p>
      </>
    );
  }

  // 招待と違うメールのアカウントでログイン中（#112）：確認してから参加
  const other = !done && info?.state === "ok" && !info.loggedInAsInvitee && info.currentEmail ? info.currentEmail : null;
  const otherDialog = other && info ? (
    <div className="dim">
      <div className="dlg invother" role="alertdialog" aria-modal="true" aria-labelledby="inv-other-h" aria-describedby="inv-other-b" data-testid="invite-other">
        <div className="dh" id="inv-other-h">{OAUTH_MSG.inviteOtherTitle}</div>
        <div className="db">
          <p id="inv-other-b" style={{ margin: 0 }}>{OAUTH_MSG.inviteOtherBody(info.email, other)}</p>
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button
            type="button"
            className="btn text"
            disabled={join.isPending || logout.isPending}
            onClick={() => {
              setErr(undefined);
              logout.mutate(undefined, { onSettled: () => void q.refetch() });
            }}
            data-testid="invite-other-switch"
          >
            {OAUTH_MSG.inviteOtherSwitch}
          </button>
          <button
            type="button"
            className="btn primary"
            autoFocus
            disabled={join.isPending || logout.isPending}
            onClick={() => {
              setErr(undefined);
              join.mutate({ token, confirm: true });
            }}
            data-testid="invite-other-join"
          >
            {OAUTH_MSG.inviteOtherJoin}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // b) 期限切れ・無効・取り消し・使用済み（#186）：止めの画面（ロゴ・絵・題・本文・「ログイン画面へ」）
  if (!done && info && info.state !== "ok" && !(TOKEN_RE.test(token) && q.isPending)) return <InviteStop state={info.state} />;

  return (
    <div className="authpage" data-product={P.product}>
      <header className="authtop">
        <a href="/" className="brand">
          <img src={P.assets.logoHeader} alt={P.name} width={320} height={100} />
        </a>
        <span className="sp" />
      </header>
      <main className="auth">
        <form className="authcard invcard" id="invcard" noValidate onSubmit={submit} data-step={done ? "done" : String(step)}>
          {/* スマホ版は上の帯を出さないので、枠の頭にロゴ（#115） */}
          <AuthLogo phoneOnly />
          {body}
        </form>
      </main>
      <SiteFooter band />
      {otherDialog}
    </div>
  );
}
